/**
 * PHASE 9 — SeeIt-mode guidance.
 *
 * Pure computation steering the user toward a searched object: where the
 * object sits relative to the camera, which way to pan, and whether it is
 * currently above the horizon. The DOM layer (seeItOverlay.ts) renders this
 * as a centre reticle + edge arrow.
 *
 * All frame maths reuse the renderer's exact matrices, so the guide matches
 * what is drawn on the sky.
 */

import type { Quat } from "../orientation/quaternion";
import { rotationFromQuat } from "../render/mat4";
import {
  buildEqdToEnuMatrix,
  buildPrecessionMatrix,
  equatorialVector,
  transformDir,
} from "../render/skyFrame";

export interface SeeItContext {
  qCamera: Quat | null;
  latitudeDeg: number;
  localSiderealHours: number;
  j2000ToOfDate: { rot: readonly (readonly number[])[] } | null;
  /** J2000 object position. */
  raHours: number;
  decDeg: number;
  fovYDeg: number;
  aspect: number;
}

export type SeeItHint = "center" | "pan" | "below-horizon" | "calibrating";

export interface SeeItState {
  /** True when the object is inside the camera's field of view. */
  onScreen: boolean;
  /** Angular distance from the screen centre (radians). */
  offsetRad: number;
  /**
   * Screen-space bearing of the object, 0° = straight up, 90° = right,
   * measures clockwise through the device screen plane.
   */
  screenAngleDeg: number;
  hint: SeeItHint;
  /** True while the camera reports no orientation. */
  calibrating: boolean;
  altitudeDeg: number;
  azimuthDeg: number;
  /** Device-frame direction of the object (unit). */
  deviceDir: readonly [number, number, number];
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Where a J2000 sky position sits relative to the camera, in the same frame
 * the renderer uses. Returns null only when the camera is unavailable.
 */
export function seeItState(ctx: SeeItContext): SeeItState | null {
  if (!ctx.qCamera) return null;

  const vJ2000 = equatorialVector(ctx.raHours, ctx.decDeg);
  const pre = ctx.j2000ToOfDate ? buildPrecessionMatrix(ctx.j2000ToOfDate) : undefined;
  const vEqd = pre ? transformDir(pre, vJ2000) : vJ2000;
  const vEnu = transformDir(buildEqdToEnuMatrix(ctx.latitudeDeg, ctx.localSiderealHours), vEqd);
  const vDevice = transformDir(rotationFromQuat(ctx.qCamera), vEnu);

  const altitudeDeg = (Math.asin(clamp(vEnu[2], -1, 1)) * 180) / Math.PI;
  // Engine azimuth convention: clockwise from true north, ENU x = east,
  // y = north -> atan2(east, north).
  let azimuthDeg = (Math.atan2(vEnu[0], vEnu[1]) * 180) / Math.PI;
  if (azimuthDeg < 0) azimuthDeg += 360;

  const offsetRad = Math.acos(clamp(-vDevice[2], -1, 1));
  const halfFovRad = ((ctx.fovYDeg / 2) * Math.PI) / 180;
  // The camera/lens looks along device -z (projection.ts); the visible sky
  // is the -z half, exactly what the renderer draws.
  const onScreen = offsetRad <= halfFovRad && vDevice[2] < 0;

  let screenAngleDeg = (Math.atan2(vDevice[0], vDevice[1]) * 180) / Math.PI;
  if (screenAngleDeg < 0) screenAngleDeg += 360;

  let hint: SeeItHint = "pan";
  if (altitudeDeg < 0 && !onScreen) hint = "below-horizon";
  else if (onScreen) hint = "center";

  return {
    onScreen,
    offsetRad,
    screenAngleDeg,
    hint,
    calibrating: false,
    altitudeDeg,
    azimuthDeg,
    deviceDir: vDevice,
  };
}

/**
 * The screen position (pixels) of the edge-of-screen panning arrow for an
 * object outside the view: sits on the viewing circle, pointing along the
 * object's screen bearing.
 */
export function arrowScreenPoint(
  widthPx: number,
  heightPx: number,
  screenAngleDeg: number,
  insetPx: number,
): { xPx: number; yPx: number } {
  const cx = widthPx / 2;
  const cy = heightPx / 2;
  const rad = Math.PI / 180;
  const angle = screenAngleDeg * rad;
  // Bearing 0° = up, 90° = right; convert to standard atan2 (x,y) coords.
  const dirX = Math.sin(angle);
  const dirY = -Math.cos(angle);
  const hw = widthPx / 2 - insetPx;
  const hh = heightPx / 2 - insetPx;
  const scale = Math.min(hw / Math.max(1e-6, Math.abs(dirX)), hh / Math.max(1e-6, Math.abs(dirY)));
  return { xPx: cx + dirX * scale, yPx: cy + dirY * scale };
}